import type { Dispatch, SetStateAction } from "react";
import type { NavigateFunction } from "react-router-dom";
import type { TFunction } from "i18next";
import type { QueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";
import {
  adminCompleteElectionAdminElectionsCompletePost,
  adminRemoveMayorAdminElectionsRemoveMayorPost,
  registerCandidateElectionsRegisterPost,
  type CityHallStateResponse,
} from "../../client";
import type { MapObject } from "../../models/mapObject";
import { handleApiError } from "../../utils/net/apiError";
import { showAlert, showConfirm } from "../../utils/telegram/alert";
import useInterfaceStore from "../../stores/useInterfaceStore";
import type { InsufficientCurrencyError } from "../../ui/modals/InsufficientPearlsModal";

/*
 * Write actions of the City Hall screen, grouped by what they mean rather than
 * by which state they touch. All three are plain factories, not hooks: the
 * originals were plain function declarations recreated on every render, so
 * keeping them hook-free preserves both that identity behaviour and the
 * screen's hook/effect ordering.
 */

export interface CityHallNavigation {
  handleClose: () => void;
  handleGoToFreePlot: () => void;
}

/** Leaving the town hall: plain close, and close-then-fly-to-a-free-plot. */
export function createCityHallNavigation({
  freePlots,
  navigate,
  onClose,
}: {
  freePlots: MapObject[];
  navigate: NavigateFunction;
  onClose: (() => void) | undefined;
}): CityHallNavigation {
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate("/map");
    }
  };

  // Closes the town hall, switches to the map view, and asks MapCanvas's existing
  // deep-link focus effect to fly to a free plot (falls back to the nearest free
  // plot to the current viewport if this exact one gets taken before it loads).
  const handleGoToFreePlot = () => {
    const target = freePlots[0];
    if (!target) return;
    useInterfaceStore.getState().setPendingDeepLinkFocus({ type: 'plot', mapObjectId: target.id });
    useInterfaceStore.getState().setMapView();
    handleClose();
  };

  return { handleClose, handleGoToFreePlot };
}

/** Standing for election: pay pearls, become a candidate. */
export function createRegisterCandidateAction({
  queryClient,
  refetch,
  setCurrentScreen,
  setError,
  setInsufficientCurrencyError,
  setIsLoading,
  setRegisterSuccessCandidateId,
  state,
  t,
  triggerConfetti,
}: {
  queryClient: QueryClient;
  refetch: () => Promise<unknown>;
  setCurrentScreen: (screen: "register_success") => void;
  setError: Dispatch<SetStateAction<string | null>>;
  setInsufficientCurrencyError: Dispatch<SetStateAction<InsufficientCurrencyError | null>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setRegisterSuccessCandidateId: Dispatch<SetStateAction<number | null>>;
  state: CityHallStateResponse | undefined;
  t: TFunction;
  triggerConfetti: () => void;
}): () => Promise<void> {
  const handleRegister = async () => {
    if (!state) return;
    setIsLoading(true);
    setError(null);

    try {
      const { data: response } = await registerCandidateElectionsRegisterPost({
        body: { payment_method: 'pearls' },
        throwOnError: true,
      });

      if (response.candidate) {
        setRegisterSuccessCandidateId(response.candidate.id);
        setCurrentScreen("register_success");
        triggerConfetti();
        setIsLoading(false);
        posthog.capture('election_action', {
          action_type: 'register',
          currency: 'pearls',
          price: state.registration_price_pearls,
        });
        void queryClient.invalidateQueries({ queryKey: ['cityhall-state'] });
        void queryClient.invalidateQueries({ queryKey: ['userData'] });
        void refetch();
      } else {
        setError(t("Failed to register as a candidate. Please try again."));
        setIsLoading(false);
      }
    } catch (err: unknown) {
      console.error('[CityHallScreen] Error registering:', err);
      const apiErr = handleApiError(err, { logLabel: 'registerCandidate' });
      if (apiErr.errorType === 'insufficient_currency') {
        setInsufficientCurrencyError({
          error_type: 'insufficient_currency',
          required_pearls: apiErr.extra.required_pearls ?? null,
          available_pearls: apiErr.extra.available_pearls ?? null,
          required_stars: apiErr.extra.required_stars ?? null,
          available_stars: apiErr.extra.available_stars ?? null,
        });
        setIsLoading(false);
        return;
      }
      setError(t("Failed to register as candidate. Please try again."));
      setIsLoading(false);
    }
  };

  return handleRegister;
}

export interface ElectionAdminActions {
  handleCompleteElection: () => Promise<void>;
  handleRemoveMayor: () => Promise<void>;
}

/** Admin-only election lifecycle: close the vote, or vacate the office. */
export function createElectionAdminActions({
  queryClient,
  refetch,
  setCurrentScreen,
  setError,
  setIsLoading,
  t,
}: {
  queryClient: QueryClient;
  refetch: () => Promise<unknown>;
  setCurrentScreen: (screen: "main") => void;
  setError: Dispatch<SetStateAction<string | null>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  t: TFunction;
}): ElectionAdminActions {
  const handleCompleteElection = async () => {
    if (!(await showConfirm(t("Are you sure you want to complete the current election? This will determine the winner and appoint them as mayor.")))) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data: response } = await adminCompleteElectionAdminElectionsCompletePost({
        throwOnError: true,
      });
      await queryClient.invalidateQueries({ queryKey: ['cityhall-state'] });
      await refetch();
      showAlert(response.message);
      setCurrentScreen("main");
    } catch (err: unknown) {
      console.error('[CityHallScreen] Error completing election:', err);
      const apiErr = handleApiError(err, { logLabel: 'completeElection' });
      setError(apiErr.detail || t("Failed to complete election"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveMayor = async () => {
    if (!(await showConfirm(t("Are you sure you want to remove the current mayor? This will clear the office and allow new candidates to register.")))) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data: response } = await adminRemoveMayorAdminElectionsRemoveMayorPost({
        throwOnError: true,
      });
      await queryClient.invalidateQueries({ queryKey: ['cityhall-state'] });
      await refetch();
      showAlert(response.message);
      setCurrentScreen("main");
    } catch (err: unknown) {
      console.error('[CityHallScreen] Error removing mayor:', err);
      const apiErr = handleApiError(err, { logLabel: 'removeMayor' });
      setError(apiErr.detail || t("Failed to remove mayor"));
    } finally {
      setIsLoading(false);
    }
  };

  return { handleCompleteElection, handleRemoveMayor };
}
